import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addDecedentNoteItem,
  createDecedentNote,
  deleteDecedentNote,
  deleteDecedentNoteItem,
  getDecedentNotes,
  updateDecedentNote,
  updateDecedentNoteItemState,
} from '@/entities/general-notes/general-notes.api';
import { generalNotesQueryKeys } from '@/entities/general-notes/general-notes.keys';

export const useGeneralNotesQuery = (decedentId, options = {}) =>
  useQuery({
    queryKey: generalNotesQueryKeys.byDecedent(decedentId),
    queryFn: () => getDecedentNotes(decedentId),
    enabled: !!decedentId,
    ...options,
  });

const invalidateGeneralNotes = (queryClient, decedentId) =>
  queryClient.invalidateQueries({
    queryKey: generalNotesQueryKeys.byDecedent(decedentId),
  });

export const useCreateGeneralNoteMutation = (decedentId, options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: (payload) => createDecedentNote(decedentId, payload),
    onSuccess: async (...args) => {
      await invalidateGeneralNotes(queryClient, decedentId);
      await onSuccess?.(...args);
    },
    ...mutationOptions,
  });
};

export const useUpdateGeneralNoteMutation = (decedentId, options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: ({ noteId, payload }) =>
      updateDecedentNote(decedentId, noteId, payload),
    onSuccess: async (...args) => {
      await invalidateGeneralNotes(queryClient, decedentId);
      await onSuccess?.(...args);
    },
    ...mutationOptions,
  });
};

export const useDeleteGeneralNoteMutation = (decedentId, options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: (noteId) => deleteDecedentNote(decedentId, noteId),
    onSuccess: async (...args) => {
      await invalidateGeneralNotes(queryClient, decedentId);
      await onSuccess?.(...args);
    },
    ...mutationOptions,
  });
};

export const useAddGeneralNoteItemMutation = (decedentId, options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: ({ noteId, payload }) =>
      addDecedentNoteItem(decedentId, noteId, payload),
    onSuccess: async (...args) => {
      await invalidateGeneralNotes(queryClient, decedentId);
      await onSuccess?.(...args);
    },
    ...mutationOptions,
  });
};

export const useDeleteGeneralNoteItemMutation = (decedentId, options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: ({ noteId, itemId }) =>
      deleteDecedentNoteItem(decedentId, noteId, itemId),
    onSuccess: async (...args) => {
      await invalidateGeneralNotes(queryClient, decedentId);
      await onSuccess?.(...args);
    },
    ...mutationOptions,
  });
};

export const useUpdateGeneralNoteItemStateMutation = (
  decedentId,
  options = {}
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: ({ noteId, itemId, payload }) =>
      updateDecedentNoteItemState(decedentId, noteId, itemId, payload),
    onSuccess: async (...args) => {
      await invalidateGeneralNotes(queryClient, decedentId);
      await onSuccess?.(...args);
    },
    ...mutationOptions,
  });
};
